import { useEffect, useState } from 'react';
import { useSearchParams, Link as RouterLink } from 'react-router-dom';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import Button from '@mui/material/Button';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import RestaurantIcon from '@mui/icons-material/Restaurant';
import { identityApiClient } from '../services/apiClient';

interface ConfirmEmailResponse {
  success: boolean;
  message: string;
}

export default function ConfirmEmail() {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [message, setMessage] = useState('');

  useEffect(() => {
    const confirmEmail = async () => {
      const userId = searchParams.get('userId');
      const code = searchParams.get('code');

      if (!userId || !code) {
        setStatus('error');
        setMessage('Neplatný odkaz pro potvrzení e-mailu.');
        return;
      }

      try {
        const response = await identityApiClient.post<{ userId: string; code: string }, ConfirmEmailResponse>(
          '/api/Account/confirm-email',
          { userId, code },
          false
        );

        if (response.success) {
          setStatus('success');
          setMessage(response.message || 'E-mail byl úspěšně potvrzen.');
        } else {
          setStatus('error');
          setMessage(response.message || 'Potvrzení e-mailu selhalo.');
        }
      } catch (error) {
        console.error('Email confirmation error:', error);
        setStatus('error');
        setMessage('Došlo k chybě při potvrzování e-mailu. Zkuste to prosím znovu.');
      }
    };

    confirmEmail();
  }, [searchParams]);

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '60vh',
      }}
    >
      <Paper
        elevation={0}
        sx={{
          p: 4,
          maxWidth: 500,
          width: '100%',
          textAlign: 'center',
          borderRadius: 3,
        }}
      >
        {status === 'loading' && (
          <>
            <CircularProgress size={64} sx={{ mb: 3 }} />
            <Typography variant="h5" gutterBottom fontWeight={600}>
              Potvrzuji e-mail...
            </Typography>
            <Typography color="text.secondary">
              Prosím počkejte, ověřujeme vaši e-mailovou adresu.
            </Typography>
          </>
        )}

        {status === 'success' && (
          <>
            <Box
              sx={{
                width: 80,
                height: 80,
                borderRadius: '50%',
                backgroundColor: 'success.light',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mx: 'auto',
                mb: 3,
              }}
            >
              <CheckCircleIcon sx={{ fontSize: 48, color: 'success.main' }} />
            </Box>
            <Typography variant="h5" gutterBottom fontWeight={600} color="success.main">
              E-mail potvrzen!
            </Typography>
            <Typography color="text.secondary" sx={{ mb: 3 }}>
              {message}
            </Typography>
            <Typography color="text.secondary" sx={{ mb: 3 }}>
              Nyní se můžete přihlásit a začít plánovat svá jídla.
            </Typography>
            <Button
              component={RouterLink}
              to="/login"
              variant="contained"
              size="large"
              startIcon={<RestaurantIcon />}
              sx={{ mt: 2 }}
            >
              Přihlásit se
            </Button>
          </>
        )}

        {status === 'error' && (
          <>
            <Box
              sx={{
                width: 80,
                height: 80,
                borderRadius: '50%',
                backgroundColor: 'error.light',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mx: 'auto',
                mb: 3,
              }}
            >
              <ErrorIcon sx={{ fontSize: 48, color: 'error.main' }} />
            </Box>
            <Typography variant="h5" gutterBottom fontWeight={600} color="error.main">
              Potvrzení selhalo
            </Typography>
            <Typography color="text.secondary" sx={{ mb: 3 }}>
              {message}
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
              <Button
                component={RouterLink}
                to="/login"
                variant="outlined"
              >
                Přihlásit se
              </Button>
              <Button
                component={RouterLink}
                to="/register"
                variant="contained"
              >
                Registrovat znovu
              </Button>
            </Box>
          </>
        )}
      </Paper>
    </Box>
  );
}
